import { useEffect } from "react";
import { useEffectEvent } from "../hooks/useEffectEvent";
import { assertIsClient } from "./env";

type AnyFunction = (...args: any[]) => any;
type Subscriber = (...args: unknown[]) => unknown;

export class PubSub<T extends AnyFunction = Subscriber> {
    private subscribers: T[];

    constructor() {
        this.subscribers = [];

        this.subscribe = this.subscribe.bind(this);
        this.unsubscribe = this.unsubscribe.bind(this);
        this.publish = this.publish.bind(this);
    }

    subscribe(subscriber: T) {
        this.subscribers.push(subscriber);

        return () => this.unsubscribe(subscriber);
    }

    private unsubscribe(subscriber: T) {
        this.subscribers = this.subscribers.filter((sub) => sub !== subscriber);
    }

    publish(...args: Parameters<T>) {
        this.subscribers.forEach((subscriber) => {
            subscriber(...args);
        });
    }
}

export const createPubSubHooks = <T extends AnyFunction>() => {
    const pubSub = new PubSub<T>();

    // Hooks ensure proper lifecycle management and prevent memory leaks
    // during SSR phase
    const usePublish = () =>
        useEffectEvent((...args: Parameters<T>) => {
            assertIsClient("Publish events should be triggered only within browser");
            pubSub.publish(...args);
        });

    const useSubscribe = (callback: T) => {
        useEffect(() => {
            const cleanup = pubSub.subscribe(callback);
            return cleanup;
        }, [callback]);
    };
    return [usePublish, useSubscribe] as const;
};
